import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowUpIcon, RotateCcwIcon, SparklesIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { ATNA_DATA_SUGGESTIONS, answerDataQuestion, type AtnaDataAnswer, type AtnaDataScope } from '../../utils/atnaDataAnswers';
interface Turn {
  id: number;
  question: string;
  answer: AtnaDataAnswer | null;
}
const TONE_BAR: Record<string, string> = {
  ok: 'bg-emerald-500',
  risk: 'bg-amber-500',
  late: 'bg-red-500',
  neutral: 'bg-[#4F46E5]'
};

/** ATNA panel: ask data questions about requests in natural language. */
export function AtnaDataAssistant({
  scope,
  externalQuestion
}: {
  scope: AtnaDataScope;
  /** A question asked from elsewhere on the page (e.g. a KPI card). */
  externalQuestion?: {
    id: number;
    text: string;
  } | null;
}) {
  const {
    t,
    lang
  } = useLanguage();
  const [input, setInput] = useState('');
  const [turns, setTurns] = useState<Turn[]>([]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const thinking = turns.some(turn => turn.answer === null);
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [turns]);

  // A new account/scope starts a fresh conversation.
  const scopeKey = scope.label?.fr ?? 'city';
  useEffect(() => setTurns([]), [scopeKey]);
  const ask = (question: string) => {
    const q = question.trim();
    if (!q || thinking) return;
    const id = Date.now();
    setTurns(prev => [...prev, {
      id,
      question: q,
      answer: null
    }]);
    setInput('');
    window.setTimeout(() => {
      setTurns(prev => prev.map(turn => turn.id === id ? {
        ...turn,
        answer: answerDataQuestion(q, scope)
      } : turn));
    }, 700);
  };
  const externalId = externalQuestion?.id;
  useEffect(() => {
    if (externalQuestion) ask(externalQuestion.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalId]);
  return <section aria-label={t('Questions de données à ATNA', 'Data questions for ATNA')} className="border border-slate-200 rounded-xl shadow-sm bg-white flex flex-col h-[360px] lg:h-auto lg:absolute lg:inset-0" data-id="element-5341" data-mp="1ysf3s1v438xa">
      <header className="flex items-center justify-between gap-3 px-5 py-3 border-b border-slate-100" data-id="element-5342" data-mp="1otzbvwo9fvmq">
        <div className="flex items-center gap-2.5" data-id="element-5343" data-mp="1ts0vcipllwyq">
          <span className="w-8 h-8 rounded-lg bg-[#4F46E5] text-white flex items-center justify-center" data-id="element-5344" data-mp="1g2gtrg7r75r0">
            <SparklesIcon className="w-4 h-4" data-id="element-5345" data-mp="1ktfhv1yzh84t" />
          </span>
          <div data-id="element-5346" data-mp="1kuqjav4yi3sr">
            <div className="text-sm font-bold text-[#0F172A] font-heading" data-id="element-5347" data-mp="164129c4tdugz">ATNA</div>
            <div className="text-xs text-slate-500 font-body" data-id="element-5348" data-mp="1626jo4h0meg2">
              {t('Posez une question sur vos données', 'Ask a question about your data')}
            </div>
          </div>
        </div>
        {turns.length > 0 && <button type="button" onClick={() => setTurns([])} aria-label={t('Nouvelle conversation', 'New conversation')} title={t('Nouvelle conversation', 'New conversation')} className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-id="element-5349" data-mp="115g6pq0ibyyv">
            <RotateCcwIcon className="w-4 h-4" data-id="element-5350" data-mp="rivp9imtaokt" />
          </button>}
      </header>

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-5 py-3 space-y-4 font-body" aria-live="polite" data-id="element-5351" data-mp="mhg47qkmwi2f">
        {turns.length === 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-id="element-5352" data-mp="wfcc89i9ywwb">
            {ATNA_DATA_SUGGESTIONS.map(s => <button key={s.fr} type="button" onClick={() => ask(s[lang])} className="text-left text-xs leading-snug text-slate-700 px-3 py-2 rounded-lg border border-slate-200 hover:border-[#4F46E5] hover:bg-indigo-50 hover:text-[#4F46E5] transition-colors" data-id="element-5353" data-mp="1a6il2w7g26vv">
                {s[lang]}
              </button>)}
          </div> : <AnimatePresence initial={false} data-id="element-5354" data-mp="1z76qz6j0jjmv">
            {turns.map(turn => <motion.div key={turn.id} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.18
        }} className="space-y-2" data-id="element-5355" data-mp="238s1wmbzlnuf">
                <div className="flex justify-end" data-id="element-5356" data-mp="1ganh2f0dxrp9">
                  <p className="max-w-[85%] text-sm bg-slate-100 text-slate-800 px-3 py-2 rounded-lg rounded-br-sm" data-id="element-5357" data-mp="1gc899xz88yjy">
                    {turn.question}
                  </p>
                </div>
                {turn.answer === null ? <div className="flex items-center gap-1.5 text-xs text-slate-400 pl-1" data-id="element-5358" data-mp="1uag0rqdhv7nq">
                    <SparklesIcon className="w-3.5 h-3.5 text-[#4F46E5] animate-pulse" data-id="element-5359" data-mp="30q8tha7urtm6" />
                    {t('ATNA analyse les données…', 'ATNA is analyzing the data…')}
                  </div> : <AnswerCard answer={turn.answer} data-id="element-5360" data-mp="u1zut3p3gtg2" />}
              </motion.div>)}
          </AnimatePresence>}
      </div>

      <form onSubmit={e => {
      e.preventDefault();
      ask(input);
    }} className="p-3 border-t border-slate-100" data-id="element-5361" data-mp="p4zn6ocljimu">
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 focus-within:border-[#4F46E5] focus-within:ring-2 focus-within:ring-indigo-100 pl-3 pr-1.5 py-1.5" data-id="element-5362" data-mp="fc2awzy76pwo">
          <input value={input} onChange={e => setInput(e.target.value)} placeholder={t('Ex. : quels problèmes reviennent le plus ?', 'e.g. which issues come up most?')} aria-label={t('Question pour ATNA', 'Question for ATNA')} className="flex-1 min-w-0 text-sm bg-transparent focus:outline-none font-body" data-id="element-5363" data-mp="6bvzdjm28005" />
          <button type="submit" disabled={!input.trim() || thinking} aria-label={t('Envoyer', 'Send')} className="w-8 h-8 rounded-full bg-[#4F46E5] text-white flex items-center justify-center hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 transition-colors" data-id="element-5364" data-mp="1qs8uwq5xu76g">
            <ArrowUpIcon className="w-4 h-4" data-id="element-5365" data-mp="3bbcobtplp4v1" />
          </button>
        </div>
      </form>
    </section>;
}
function AnswerCard({
  answer
}: {
  answer: AtnaDataAnswer;
}) {
  const {
    lang
  } = useLanguage();
  const max = answer.max ?? 100;
  return <div className="border border-indigo-100 bg-indigo-50/40 rounded-lg p-3 space-y-3" data-id="element-5366" data-mp="csdyxcp7qri8">
      <p className="text-sm text-slate-700 leading-relaxed" data-id="element-5367" data-mp="cq1m1dybb7c1">{answer.text[lang]}</p>
      {answer.rows && <ul className="space-y-1.5" data-id="element-5368" data-mp="2u7zr2usjzh2">
          {answer.rows.map((row, i) => <li key={`${row.label.fr}-${i}`} className="grid grid-cols-[minmax(0,1fr)_90px_48px] items-center gap-2 text-xs" data-id="element-5369" data-mp="2waxk926qwwc" data-mpi={i}>
              <span className="text-slate-600 truncate" data-id="element-5370" data-mp="2nk78vfnkoel9">{row.label[lang]}</span>
              <span className="h-1.5 rounded-full bg-slate-200 overflow-hidden" data-id="element-5371" data-mp="2ni4943wmb73c">
                <span className={`block h-full rounded-full ${TONE_BAR[row.tone]}`} style={{
            width: `${Math.min(100, row.value / max * 100)}%`
          }} data-id="element-5372" data-mp="2g30w4paq579w" />
              </span>
              <span className="text-right font-semibold text-slate-800 tabular-nums" data-id="element-5373" data-mp="2l011exzr25zy">
                {row.value.toLocaleString(lang === 'fr' ? 'fr-CA' : 'en-CA')}
                {row.unit}
              </span>
            </li>)}
        </ul>}
      <p className="text-[11px] text-slate-400" data-id="element-5374" data-mp="1scwuuol0djm2">{answer.source[lang]}</p>
    </div>;
}